import { faMoneyCheckDollarPen } from '@fortawesome/pro-regular-svg-icons'

import { TextColors, TypographyStyles } from '@/styles'
import { Icon, Text } from '@/ui'
import { formatCurrencyFromCents } from '@/utils/currencyHelpers'

import { BreakdownTooltip } from '../estimate-breakdown-v3/shared/breakdown-tooltip'
import { CalculationRow } from '../estimate-breakdown-v3/shared/calculation-row'
import {
  StatePaymentsCreditedTooltip,
  StateWithholdingTooltip,
} from './StateBreakdownTooltips'
import { PaymentGateCallout } from '../PaymentGateCallout'
import { centsToSubtractedAmount } from '../utils'
import type { StateBreakdownData } from './stateBreakdownData'

/**
 * Explains the withheld figure in a sentence rather than showing the entered
 * and projected amounts as two bare columns, which read as a range.
 */
const WithholdingProjectionNote = ({
  entryInCents,
  projectedInCents,
}: {
  entryInCents: number | null
  projectedInCents: number
}) => {
  if (!entryInCents) {
    return null
  }

  return (
    <Text color={TextColors.MUTED}>
      <i>
        {formatCurrencyFromCents(entryInCents)} has come out of those paychecks
        so far. We expect {formatCurrencyFromCents(projectedInCents)} by the end
        of the quarter, so that&apos;s the amount we count.
      </i>
    </Text>
  )
}

export const StateTaxesPaidSection = ({
  data,
  quarter,
  hasSpouse,
}: {
  data: StateBreakdownData
  quarter: string | undefined
  hasSpouse: boolean
}) => (
  <>
    <div className="flex items-center mb-4">
      <Text variant={TypographyStyles.H2} className="flex items-center">
        <Icon icon={faMoneyCheckDollarPen} className="mr-2" />
        Taxes Paid
      </Text>
    </div>

    <Text className="w-full lg:w-8/12 mb-6" color={TextColors.MUTED}>
      We subtract what you&apos;ve already paid toward {data.state} this year.
    </Text>

    <PaymentGateCallout
      allQuarterPaymentsLogged={data.allQuarterPaymentsLogged}
      quarter={quarter}
    />

    {data.creditedPaymentsInCents !== null && (
      <CalculationRow
        left={
          <BreakdownTooltip
            trigger={`${data.state} payments you've already made`}
            content={<StatePaymentsCreditedTooltip filingState={data.state} />}
          />
        }
        right={
          <Text variant={TypographyStyles.H3}>
            {centsToSubtractedAmount(data.creditedPaymentsInCents)}
          </Text>
        }
      />
    )}

    {data.withholding ? (
      <>
        <CalculationRow
          left={
            <>
              <BreakdownTooltip
                trigger={`${data.state} tax from your paychecks`}
                content={<StateWithholdingTooltip filingState={data.state} />}
              />
              <WithholdingProjectionNote
                entryInCents={data.withholding.entryInCents}
                projectedInCents={data.withholding.projectedInCents}
              />
            </>
          }
          right={
            <Text variant={TypographyStyles.H3}>
              {centsToSubtractedAmount(data.withholding.projectedInCents)}
            </Text>
          }
        />
        {hasSpouse && (
          <CalculationRow
            left={
              <>
                <Text>{data.state} tax from your spouse&apos;s paychecks</Text>
                <WithholdingProjectionNote
                  entryInCents={data.withholding.spouseEntryInCents}
                  projectedInCents={data.withholding.projectedSpouseInCents}
                />
              </>
            }
            right={
              <Text variant={TypographyStyles.H3}>
                {centsToSubtractedAmount(
                  data.withholding.projectedSpouseInCents
                )}
              </Text>
            }
          />
        )}
      </>
    ) : (
      <CalculationRow
        left={
          <>
            <Text>{data.state} tax from your paychecks</Text>
            <Text color={TextColors.MUTED}>
              <i>
                Tax taken out of your paychecks counts toward the state you live
                in, not {data.state}, so it doesn&apos;t lower this amount.
              </i>
            </Text>
          </>
        }
        right={<Text variant={TypographyStyles.H3}>$0.00</Text>}
      />
    )}
  </>
)
